import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { API_URL } from '../config/constants';

interface LegalPageData {
  id: number;
  type: string;
  title: string;
  content: string;
  isActive: boolean;
  version: number;
  publishedAt: string;
  updatedAt: string;
}

interface DynamicLegalPageProps {
  pageType: 'privacy' | 'cookies' | 'terms' | 'copyright';
}

const DynamicLegalPage: React.FC<DynamicLegalPageProps> = ({ pageType }) => {
  const [pageData, setPageData] = useState<LegalPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const pageIcons = {
    privacy: '🔒',
    cookies: '🍪',
    terms: '📄',
    copyright: '⚖️'
  };

  const defaultTitles = {
    privacy: 'Politica de Confidențialitate',
    cookies: 'Politica de Cookies',
    terms: 'Termeni și Condiții',
    copyright: 'Protecția Drepturilor de Autor'
  };

  useEffect(() => {
    fetchPageData();
  }, [pageType]);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/legal-pages/${pageType}`);
      
      if (response.ok) {
        const data = await response.json();
        setPageData(data.data);
      } else if (response.status === 404) {
        setError('Pagina nu a fost găsită');
      } else {
        setError('Eroare la încărcarea paginii');
      }
    } catch (error) {
      console.error('Eroare la încărcarea paginii:', error);
      setError('Eroare la încărcarea paginii');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 md:pt-28"></div>
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Se încarcă...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error && !pageData) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 md:pt-28"></div>
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="text-6xl mb-4">😔</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Pagina nu a fost găsită
            </h2>
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
            >
              Înapoi
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      {/* Custom styles for legal content */}
      <style>{`
        .legal-content section {
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-radius: 0.5rem;
          border-left: 4px solid #3b82f6;
        }
        
        .legal-content section h2 {
          color: #1e40af !important;
          margin-top: 0 !important;
          margin-bottom: 1rem !important;
          font-size: 1.5rem !important;
          font-weight: 700 !important;
        }
        
        .legal-content section p {
          margin-bottom: 0.75rem !important;
          line-height: 1.6 !important;
        }
        
        .legal-content section:last-child {
          margin-bottom: 0;
        }
        
        /* Fallback styling for non-section content */
        .legal-content > h1 {
          color: #1f2937 !important;
          font-size: 2rem !important;
          font-weight: 800 !important;
          margin-bottom: 2rem !important;
          padding-bottom: 1rem !important;
          border-bottom: 2px solid #e5e7eb !important;
        }
        
        .legal-content > h2 {
          color: #1e40af !important;
          font-size: 1.5rem !important;
          font-weight: 700 !important;
          margin-top: 2rem !important;
          margin-bottom: 1rem !important;
        }
        
        .legal-content > h3 {
          color: #374151 !important;
          font-size: 1.25rem !important;
          font-weight: 600 !important;
          margin-top: 1.5rem !important;
          margin-bottom: 0.75rem !important;
        }
        
        .legal-content > p {
          color: #4b5563 !important;
          line-height: 1.7 !important;
          margin-bottom: 1rem !important;
        }
        
        .legal-content > ul, .legal-content > ol {
          margin-bottom: 1.5rem !important;
          padding-left: 1.5rem !important;
        }
        
        .legal-content > ul > li, .legal-content > ol > li {
          color: #4b5563 !important;
          margin-bottom: 0.5rem !important;
          line-height: 1.6 !important;
        }
        
        .legal-content strong, .legal-content b {
          color: #1f2937 !important;
          font-weight: 600 !important;
        }
        
        .legal-content a {
          color: #2563eb !important;
          text-decoration: none !important;
        }
        
        .legal-content a:hover {
          text-decoration: underline !important;
        }
        
        .legal-content blockquote {
          border-left: 4px solid #60a5fa !important;
          padding-left: 1rem !important;
          margin: 1.5rem 0 !important;
          font-style: italic !important;
          color: #6b7280 !important;
        }
        
        .legal-content code {
          background-color: #f3f4f6 !important;
          padding: 0.25rem 0.5rem !important;
          border-radius: 0.25rem !important;
          font-family: 'Courier New', monospace !important;
          color: #1f2937 !important;
        }
        
        .legal-content table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin: 1.5rem 0 !important;
        }
        
        .legal-content th, .legal-content td {
          border: 1px solid #d1d5db !important;
          padding: 0.75rem !important;
          text-align: left !important;
        }
        
        .legal-content th {
          background-color: #f9fafb !important;
          font-weight: 600 !important;
          color: #1f2937 !important;
        }
      `}</style>
      
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        
        {/* Spațiu pentru header-ul fix */}
        <div className="pt-24 md:pt-28"></div>
        
        <main className="flex-grow">
          {/* Header section */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
            <div className="container mx-auto px-4 text-center">
              <div className="text-6xl mb-4">{pageIcons[pageType]}</div>
              <h1 className="text-4xl font-bold mb-4">
                {pageData?.title || defaultTitles[pageType]}
              </h1>
              {pageData && (
                <div className="flex items-center justify-center space-x-6 text-blue-100">
                  <span>Versiunea {pageData.version}</span>
                  <span>•</span>
                  <span>
                    Actualizat pe {new Date(pageData.updatedAt).toLocaleDateString('ro-RO')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Content section */}
          <div className="container mx-auto px-4 py-12">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
                {pageData ? (
                  <div 
                    className="legal-content max-w-4xl mx-auto prose prose-blue prose-lg
                      prose-headings:text-gray-800 prose-headings:font-bold prose-headings:mb-4
                      prose-h1:text-3xl prose-h1:border-b prose-h1:border-gray-200 prose-h1:pb-4
                      prose-h2:text-2xl prose-h2:text-blue-700 prose-h2:mt-8 prose-h2:mb-4
                      prose-h3:text-xl prose-h3:text-gray-700 prose-h3:mt-6 prose-h3:mb-3
                      prose-p:text-gray-600 prose-p:leading-relaxed prose-p:mb-4
                      prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                      prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
                      prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
                      prose-li:text-gray-600 prose-li:mb-2
                      prose-strong:text-gray-800 prose-strong:font-semibold
                      prose-blockquote:border-l-4 prose-blockquote:border-blue-300 prose-blockquote:pl-6 prose-blockquote:italic
                      prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded
                      prose-table:border-collapse prose-table:w-full
                      prose-th:border prose-th:border-gray-300 prose-th:bg-gray-50 prose-th:p-3 prose-th:text-left
                      prose-td:border prose-td:border-gray-300 prose-td:p-3"
                    dangerouslySetInnerHTML={{ __html: pageData.content }}
                  />
                ) : (
                  <div className="text-center py-12">
                    <div className="text-gray-400 text-6xl mb-4">📄</div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      Pagina nu a fost găsită
                    </h3>
                    <p className="text-gray-500">
                      Pagina legală solicitată nu există sau nu este disponibilă momentan.
                    </p>
                  </div>
                )}
              </div>

              {/* Navigation buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-between">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Înapoi
                </button>

                <div className="flex gap-4">
                  {pageType !== 'terms' && (
                    <button
                      onClick={() => navigate('/terms')}
                      className="flex items-center justify-center px-6 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                    >
                      📄 Termeni și Condiții
                    </button>
                  )}
                  {pageType !== 'privacy' && (
                    <button
                      onClick={() => navigate('/privacy')}
                      className="flex items-center justify-center px-6 py-3 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                    >
                      🔒 Politica de Confidențialitate
                    </button>
                  )}
                  {pageType !== 'cookies' && (
                    <button
                      onClick={() => navigate('/cookies')}
                      className="flex items-center justify-center px-6 py-3 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition"
                    >
                      🍪 Politica de Cookies
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default DynamicLegalPage; 